import React, { useEffect, useCallback } from "react";
import { X, Download, Scale, AlertTriangle } from "lucide-react";

/**
 * ValidationDrawer — the Cockpit's right-hand validation panel.
 *
 * Renders a side panel anchored to the right edge of the viewport. The
 * backdrop dims and lightly blurs the underlying Cockpit so the user can
 * focus on the validation task for the active VNG step.
 *
 * The drawer is render-prop based: it owns the chrome (header, backdrop,
 * scroll container, footer hint) and yields the body to `children` so the
 * caller controls *what* is being validated (note textarea, document
 * uploader, IAMA copy, etc.).
 */
const RISKAIA_TEMPLATE_URL =
  "https://customer-assets.emergentagent.com/wingman/368cca20-abd0-4588-99d9-9e42b06e9661/attachments/fb23c95e2a4d4c328d13aa4d02028a93_riskaia_compliance_report.pdf";

const ValidationDrawer = ({
  open,
  onClose,
  step,
  isHighRisk = false,
  onDownloadTemplate,
  children,
}) => {
  // Esc key closes the drawer
  const onKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onKey]);

  if (!step) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        data-testid="validation-drawer-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(7, 14, 26, 0.55)",
          backdropFilter: open ? "blur(6px)" : "blur(0px)",
          WebkitBackdropFilter: open ? "blur(6px)" : "blur(0px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 280ms ease, backdrop-filter 280ms ease",
          zIndex: 90,
        }}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Validatiepaneel voor ${step.title}`}
        data-testid="validation-drawer"
        data-open={open ? "true" : "false"}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: "min(560px, 92vw)",
          background: "#162E4A",
          borderLeft: "1px solid rgba(192,197,206,0.20)",
          boxShadow: open
            ? "-40px 0 80px -30px rgba(0,0,0,0.65)"
            : "none",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <header
          className="px-7 pt-7 pb-5"
          style={{ borderBottom: "1px solid rgba(192,197,206,0.12)" }}
        >
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div
                className="overline mb-3"
                style={{ color: "#7FD7E4" }}
              >
                Validatiepaneel · Stap {step.number}
              </div>
              <h2
                className="text-white tracking-tight leading-tight text-3xl md:text-4xl"
                style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
                data-testid="validation-drawer-title"
              >
                {step.number} · {step.title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="validation-drawer-close"
              aria-label="Sluit validatiepaneel"
              className="shrink-0 w-9 h-9 flex items-center justify-center transition-colors"
              style={{
                border: "1px solid rgba(192,197,206,0.30)",
                background: "transparent",
                cursor: "pointer",
                color: "#D4D8DF",
              }}
            >
              <X strokeWidth={1.25} className="w-4 h-4" />
            </button>
          </div>

          {/* Legal tags row */}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            {step.legal && (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase px-2 py-1"
                style={{
                  border: "1px solid rgba(127,215,228,0.45)",
                  color: "#7FD7E4",
                  background: "rgba(91,192,235,0.06)",
                }}
                data-testid="validation-drawer-legal"
              >
                <Scale strokeWidth={1.25} className="w-3 h-3" />
                EU AI Act · {step.legal.article} — {step.legal.topic}
              </span>
            )}
            {step.id === "ontwerp" && isHighRisk && (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase px-2 py-1"
                style={{
                  border: "1px solid rgba(244,196,48,0.55)",
                  color: "#F4C430",
                  background: "rgba(244,196,48,0.10)",
                }}
              >
                <AlertTriangle strokeWidth={1.5} className="w-3 h-3" />
                Wettelijke IAMA-plicht
              </span>
            )}
          </div>
        </header>

        {/* Body — scrollable */}
        <div
          className="flex-1 overflow-y-auto px-7 py-7 space-y-7"
          data-testid="validation-drawer-body"
        >
          <p
            className="text-[15px] leading-relaxed"
            style={{ color: "#D4D8DF" }}
            data-testid="validation-drawer-statement"
          >
            Om deze module te valideren is een formeel
            compliance-document vereist.
          </p>

          {/* OPTIE 01 — Download het sjabloon */}
          <section data-testid="validation-drawer-option-template">
            <div
              className="overline mb-3"
              style={{ color: "#7FD7E4" }}
            >
              Optie 01 · Download het sjabloon
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (onDownloadTemplate) {
                  onDownloadTemplate();
                } else {
                  // Legacy fallback when the parent didn't wire a
                  // generator — open the shared demonstrator PDF.
                  window.open(RISKAIA_TEMPLATE_URL, "_blank", "noreferrer");
                }
              }}
              data-testid="validation-drawer-template-link"
              className="w-full text-left flex items-center gap-4 p-4 transition-colors"
              style={{
                border: "1px solid rgba(127,215,228,0.45)",
                background: "rgba(91,192,235,0.06)",
                cursor: "pointer",
              }}
            >
              <span
                className="w-10 h-10 flex items-center justify-center shrink-0"
                style={{
                  border: "1px solid rgba(127,215,228,0.55)",
                  color: "#7FD7E4",
                }}
              >
                <Download strokeWidth={1.25} className="w-4 h-4" />
              </span>
              <span className="flex-1 min-w-0">
                <span
                  className="block text-[15px]"
                  style={{
                    fontFamily: "Fraunces, serif",
                    color: "#FFFFFF",
                    fontWeight: 400,
                  }}
                >
                  Vul het officiële RiskaiΛ-sjabloon in
                </span>
                <span
                  className="block mt-1 text-[10px] tracking-[0.22em] uppercase"
                  style={{ color: "#7FD7E4" }}
                >
                  Formulier · invullen &amp; genereren · v1.2 · PDF
                </span>
              </span>
            </button>
            <p
              className="mt-3 text-[12px] leading-relaxed"
              style={{ color: "#9AA4B2" }}
            >
              Vul het sjabloon in, laat het ondertekenen door de bevoegde
              functionarissen en upload het hieronder als bewijs.
            </p>
          </section>

          {/* Subtle separator */}
          <div
            aria-hidden
            style={{
              height: 1,
              background:
                "linear-gradient(90deg, rgba(192,197,206,0.05) 0%, rgba(192,197,206,0.25) 50%, rgba(192,197,206,0.05) 100%)",
            }}
          />

          {/* OPTIE 02 — Upload + Onderbouwing (delegated to caller) */}
          <section data-testid="validation-drawer-option-upload">
            <div
              className="overline mb-3"
              style={{ color: "#7FD7E4" }}
            >
              Optie 02 · Upload bewijs &amp; onderbouwing
            </div>
            {children}
          </section>
        </div>

        {/* Footer hint */}
        <footer
          className="px-7 py-4"
          style={{
            borderTop: "1px solid rgba(192,197,206,0.12)",
            background: "rgba(15, 30, 51, 0.6)",
          }}
        >
          <p
            className="text-[10px] tracking-[0.22em] uppercase text-center"
            style={{ color: "#7FD7E4" }}
            data-testid="validation-drawer-footer"
          >
            Stap bereikt GOUD bij document &amp; onderbouwing
          </p>
        </footer>
      </aside>
    </>
  );
};

export default ValidationDrawer;
